import { useState, useEffect, useRef } from 'react';
import {
    Box,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    Tooltip,
    TextField,
    MenuItem,
    Autocomplete,
    ToggleButton,
    ToggleButtonGroup,
    Pagination,
    useTheme,
    Typography,
    Alert,
    Tab,
    Tabs
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSignOut } from 'react-auth-kit';
import Axios from 'axios';
import Header from '../../components/Header';
import Loading from '../../components/Loading';
import { tokens } from '../../theme';
import { getAuthHeader } from '../../utils/authToken';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import SyncIcon from '@mui/icons-material/Sync';
import DraftIcon from '@mui/icons-material/Edit';
import PublishIcon from '@mui/icons-material/Publish';
import { styled } from '@mui/material/styles';
import { tableCellClasses } from '@mui/material/TableCell';
import { useSnackbar } from 'notistack';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Dropdown from '../../components/dropdown/Dropdown';
import { generateYears, semesters } from '../../components/dropdown/DropdownData';
import { getCourseYear, getCourseSemester } from '../../utils/CourseDuration';
import AutoReports from './AutoReports';
import DevAutoReportTesting from './DevAutoReportTesting';
import AutoReportExplainer from './AutoReportExplainer';

// Map ?tab=<slug> query param to a tab index, and back. Keeps deep links
// stable across renders so the dev page's "Open in Admin Panel" button can
// land directly on the Auto Reports tab.
const TAB_SLUGS = ['reports', 'auto', 'dev', 'explainer'];
const slugToIndex = (slug) => {
    const idx = TAB_SLUGS.indexOf(slug);
    return idx === -1 ? 0 : idx;
};

const Reports = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const navigate = useNavigate();
    const signOut = useSignOut();
    const { enqueueSnackbar } = useSnackbar();
    const [searchParams, setSearchParams] = useSearchParams();

    const [tabValue, setTabValue] = useState(() => slugToIndex(searchParams.get('tab')));

    const handleTabChange = (_e, newValue) => {
        setTabValue(newValue);
        const next = new URLSearchParams(searchParams);
        next.set('tab', TAB_SLUGS[newValue]);
        setSearchParams(next, { replace: true });
    };
    const [coursesLoading, setCoursesLoading] = useState(false);
    const [reportsLoading, setReportsLoading] = useState(false);
    const [reports, setReports] = useState([]);
    const [courses, setCourses] = useState([]);
    const [filteredCourses, setFilteredCourses] = useState([]);
    const [error, setError] = useState('');

    // Year and semester filtering
    const [maxYear, setMaxYear] = useState(0);
    const [minYear, setMinYear] = useState(0);
    const [year, setYear] = useState('');
    const [semester, setSemester] = useState('');
    const season = {'Spring': 1, 'Summer': 2, 'Fall': 3, 'Winter': 4};

    // Filters
    const [selectedCourse, setSelectedCourse] = useState(null);
    const [reportTypeFilter, setReportTypeFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');

    // Delete dialog
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [reportToDelete, setReportToDelete] = useState(null);

    // Publish dialog
    const [publishDialogOpen, setPublishDialogOpen] = useState(false);
    const [completedReports, setCompletedReports] = useState([]);
    const [selectedReportsToPublish, setSelectedReportsToPublish] = useState([]);
    const [publishLoading, setPublishLoading] = useState(false);
    const [fetchingCompletedReports, setFetchingCompletedReports] = useState(false);

    // Polling for active reports
    const intervalRef = useRef(null);

    // View mode toggle
    const [viewMode, setViewMode] = useState('all'); // 'course' | 'all' | 'student'
    const [allReports, setAllReports] = useState([]);
    const [allReportsLoading, setAllReportsLoading] = useState(false);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0
    });

    // Student search mode
    const [students, setStudents] = useState([]);
    const [studentsLoading, setStudentsLoading] = useState(false);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [studentReports, setStudentReports] = useState([]);
    const [studentReportsLoading, setStudentReportsLoading] = useState(false);

    const StyledTableCell = styled(TableCell)(() => ({
        [`&.${tableCellClasses.head}`]: {
            backgroundColor: colors.primary[300],
            color: 'white',
            fontSize: 14,
            fontWeight: 'bold',
        },
        [`&.${tableCellClasses.body}`]: {
            fontSize: 14,
        },
        border: '1px solid rgba(224, 224, 224, 1)',
    }));

    const StyledTableRow = styled(TableRow)(() => ({
        '&:nth-of-type(odd)': {
            backgroundColor: colors.primary[750],
        },
        '&:nth-of-type(even)': {
            backgroundColor: colors.primary[750],
        },
        '&:hover': {
            backgroundColor: colors.primary[700],
        }
    }));

    const handleLogout = () => {
        signOut();
        localStorage.removeItem('userName');
        localStorage.removeItem('who');
        navigate('/signin');
    };

    // Fetch current semester from API
    const fetchCurrentSemester = async () => {
        try {
            const response = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/semesters/current-semester`,
                {
                    headers: { Authorization: getAuthHeader() }
                }
            );
            setYear(response.data.year);
            setSemester(response.data.semester);
        } catch (error) {
            if (error.response?.status === 401) {
                handleLogout();
            } else {
                console.error('Error fetching current semester:', error);
                // Fallback to current year if API fails
                setYear(new Date().getFullYear());
            }
        }
    };

    // Fetch max and min years for dropdown
    const fetchMaxMinYears = async () => {
        try {
            const response = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/semesters/max-min-years`,
                {
                    headers: { Authorization: getAuthHeader() }
                }
            );
            setMaxYear(response.data.max_year);
            setMinYear(response.data.min_year);
        } catch (error) {
            if (error.response?.status === 401) {
                handleLogout();
            } else {
                console.error('Error fetching years:', error);
            }
        }
    };

    // Fetch courses list (lightweight)
    const fetchCourses = async () => {
        try {
            setCoursesLoading(true);
            const response = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/courses/all/list`,
                {
                    headers: { Authorization: getAuthHeader() }
                }
            );
            setCourses(response.data.all_courses || []);
            setCoursesLoading(false);
        } catch (error) {
            if (error.response?.status === 401) {
                handleLogout();
            } else {
                console.error('Error fetching courses:', error);
                setError('Failed to load courses');
                setCoursesLoading(false);
            }
        }
    };

    // Fetch reports for selected course only
    const fetchReportsForCourse = async (courseUuid) => {
        try {
            setReportsLoading(true);
            setError('');
            const response = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/courses/${courseUuid}/reports`,
                {
                    headers: { Authorization: getAuthHeader() }
                }
            );
            setReports(response.data.reports || []);
            setReportsLoading(false);
        } catch (error) {
            if (error.response?.status === 401) {
                handleLogout();
            } else if (error.response?.status === 404) {
                // No reports for this course
                setReports([]);
                setError('');
                setReportsLoading(false);
            } else {
                console.error('Error fetching reports:', error);
                setError('Failed to load reports');
                setReportsLoading(false);
            }
        }
    };

    // Fetch all reports with pagination
    const fetchAllReports = async () => {
        try {
            setAllReportsLoading(true);
            setError('');

            const params = new URLSearchParams({
                page: pagination.page.toString(),
                limit: pagination.limit.toString(),
            });

            if (reportTypeFilter !== 'all') {
                params.append('report_type', reportTypeFilter);
            }
            if (statusFilter !== 'all') {
                params.append('status', statusFilter);
            }

            const response = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/reports?${params.toString()}`,
                {
                    headers: { Authorization: getAuthHeader() }
                }
            );

            setAllReports(response.data.reports || []);
            setPagination(response.data.pagination || {
                page: 1,
                limit: 20,
                total: 0,
                totalPages: 0
            });
            setAllReportsLoading(false);
        } catch (error) {
            if (error.response?.status === 401) {
                handleLogout();
            } else {
                console.error('Error fetching all reports:', error);
                setError('Failed to load reports');
                setAllReportsLoading(false);
            }
        }
    };

    // Fetch students for search
    const fetchStudentsForSearch = async () => {
        try {
            setStudentsLoading(true);
            const response = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/students/search-list`,
                {
                    headers: { Authorization: getAuthHeader() }
                }
            );
            setStudents(response.data || []);
            setStudentsLoading(false);
        } catch (error) {
            if (error.response?.status === 401) {
                handleLogout();
            } else {
                console.error('Error fetching students:', error);
                setError('Failed to load students');
                setStudentsLoading(false);
            }
        }
    };

    // Fetch reports for selected student
    const fetchStudentReports = async (studentUuid) => {
        try {
            setStudentReportsLoading(true);
            setError('');
            const response = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/students/${studentUuid}/reports`,
                {
                    headers: { Authorization: getAuthHeader() }
                }
            );
            setStudentReports(response.data.reports || []);
            setStudentReportsLoading(false);
        } catch (error) {
            if (error.response?.status === 401) {
                handleLogout();
            } else if (error.response?.status === 404) {
                setStudentReports([]);
                setError('');
                setStudentReportsLoading(false);
            } else {
                console.error('Error fetching student reports:', error);
                setError('Failed to load student reports');
                setStudentReportsLoading(false);
            }
        }
    };

    // Initialize: Fetch current semester and year ranges on mount
    useEffect(() => {
        localStorage.setItem('selectedMenu', 'Reports');
        fetchMaxMinYears();
        fetchCurrentSemester();
    }, []);

    // Fetch courses when year/semester changes
    useEffect(() => {
        if (!year || !semester) {
            setCourses([]);
            setFilteredCourses([]);
            setSelectedCourse(null);
            setReports([]);
            return;
        }

        fetchCourses();
    }, [year, semester]);

    // Filter courses based on year and semester
    useEffect(() => {
        if (courses.length === 0) {
            setFilteredCourses([]);
            return;
        }

        const filtered = courses.filter((course) => {
            // Filter by year
            if (year && !getCourseYear(course).includes(year)) {
                return false;
            }
            // Filter by semester
            if (semester !== 'all' && semester && !getCourseSemester(course, year).includes(season[semester])) {
                return false;
            }
            return true;
        });

        setFilteredCourses(filtered);

        // Reset selected course if it's not in the filtered list
        if (selectedCourse && !filtered.find(c => c.uuid === selectedCourse.uuid)) {
            setSelectedCourse(null);
        }
    }, [courses, year, semester]);

    // Fetch reports when course is selected
    useEffect(() => {
        if (!selectedCourse) {
            setReports([]);
            return;
        }

        fetchReportsForCourse(selectedCourse.uuid);
    }, [selectedCourse]);

    // Fetch all reports when in 'all' view mode
    useEffect(() => {
        if (viewMode !== 'all') {
            return;
        }

        fetchAllReports();
    }, [viewMode, pagination.page, reportTypeFilter, statusFilter]);

    // Fetch students list when in 'student' view mode
    useEffect(() => {
        if (viewMode !== 'student') {
            return;
        }

        fetchStudentsForSearch();
    }, [viewMode]);

    // Fetch student reports when student is selected
    useEffect(() => {
        if (!selectedStudent) {
            setStudentReports([]);
            return;
        }

        fetchStudentReports(selectedStudent.uuid);
    }, [selectedStudent]);

    // Conditional polling for active reports (pending/processing)
    useEffect(() => {
        // Determine which reports to check based on view mode
        let currentReports = [];
        if (viewMode === 'all') {
            currentReports = allReports;
        } else if (viewMode === 'student') {
            currentReports = studentReports;
        } else {
            currentReports = reports;
        }

        // Don't poll if we have no reports
        if (currentReports.length === 0) {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            return;
        }

        // For course mode, also need a selected course
        if (viewMode === 'course' && !selectedCourse) {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            return;
        }

        // For student mode, also need a selected student
        if (viewMode === 'student' && !selectedStudent) {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            return;
        }

        // Check if there are any active reports
        const hasActiveReports = currentReports.some(
            (report) => report.status === 'pending' || report.status === 'processing'
        );

        if (hasActiveReports && !intervalRef.current) {
            // Start polling
            console.log(`Starting report polling in ${viewMode} mode`);
            intervalRef.current = setInterval(() => {
                if (viewMode === 'all') {
                    fetchAllReports();
                } else if (viewMode === 'student') {
                    fetchStudentReports(selectedStudent.uuid);
                } else {
                    fetchReportsForCourse(selectedCourse.uuid);
                }
            }, 5000); // Poll every 5 seconds
        } else if (!hasActiveReports && intervalRef.current) {
            // Stop polling when all reports are done
            console.log('Stopping report polling - all reports completed');
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }

        // Cleanup on unmount or dependency change
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [reports, allReports, studentReports, selectedCourse, selectedStudent, viewMode]);

    const handleDeleteReport = async () => {
        if (!reportToDelete) return;

        try {
            await Axios.delete(
                `${process.env.REACT_APP_URL}/api/v1/reports/${reportToDelete}`,
                {
                    headers: { Authorization: getAuthHeader() }
                }
            );
            enqueueSnackbar('Report deleted successfully', { variant: 'success' });
            // Refresh based on view mode
            if (viewMode === 'all') {
                fetchAllReports();
            } else if (viewMode === 'student' && selectedStudent) {
                fetchStudentReports(selectedStudent.uuid);
            } else if (selectedCourse) {
                fetchReportsForCourse(selectedCourse.uuid);
            }
        } catch (error) {
            if (error.response?.status === 401) {
                handleLogout();
            } else {
                enqueueSnackbar('Failed to delete report', { variant: 'error' });
            }
        } finally {
            setDeleteDialogOpen(false);
            setReportToDelete(null);
        }
    };

    const handleRetryReport = async (reportId) => {
        try {
            await Axios.post(
                `${process.env.REACT_APP_URL}/api/v1/reports/${reportId}/retry`,
                {},
                {
                    headers: { Authorization: getAuthHeader() }
                }
            );
            enqueueSnackbar('Report generation restarted', { variant: 'success' });
            // Refresh based on view mode
            if (viewMode === 'all') {
                fetchAllReports();
            } else if (viewMode === 'student' && selectedStudent) {
                fetchStudentReports(selectedStudent.uuid);
            } else if (selectedCourse) {
                fetchReportsForCourse(selectedCourse.uuid);
            }
        } catch (error) {
            if (error.response?.status === 401) {
                handleLogout();
            } else {
                enqueueSnackbar('Failed to retry report', { variant: 'error' });
            }
        }
    };

    // Fetch all completed reports for bulk publish
    const fetchCompletedReports = async () => {
        try {
            setFetchingCompletedReports(true);
            const response = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/reports?status=completed&limit=100`,
                {
                    headers: { Authorization: getAuthHeader() }
                }
            );
            const reports = response.data.reports || [];
            setCompletedReports(reports);
            // Pre-select all completed reports
            setSelectedReportsToPublish(reports.map(r => r.uuid));
        } catch (error) {
            if (error.response?.status === 401) {
                handleLogout();
            } else {
                enqueueSnackbar('Failed to fetch completed reports', { variant: 'error' });
            }
        } finally {
            setFetchingCompletedReports(false);
        }
    };

    const handleOpenPublishDialog = () => {
        setPublishDialogOpen(true);
        fetchCompletedReports();
    };

    const handleClosePublishDialog = () => {
        setPublishDialogOpen(false);
        setCompletedReports([]);
        setSelectedReportsToPublish([]);
    };

    const handleToggleReportSelection = (reportUuid) => {
        setSelectedReportsToPublish(prev => {
            if (prev.includes(reportUuid)) {
                return prev.filter(id => id !== reportUuid);
            } else {
                return [...prev, reportUuid];
            }
        });
    };

    const handleToggleSelectAll = () => {
        if (selectedReportsToPublish.length === completedReports.length) {
            setSelectedReportsToPublish([]);
        } else {
            setSelectedReportsToPublish(completedReports.map(r => r.uuid));
        }
    };

    const handleBulkPublish = async () => {
        if (selectedReportsToPublish.length === 0) {
            enqueueSnackbar('No reports selected', { variant: 'warning' });
            return;
        }

        setPublishLoading(true);
        let successCount = 0;
        let failCount = 0;

        for (const reportUuid of selectedReportsToPublish) {
            try {
                await Axios.post(
                    `${process.env.REACT_APP_URL}/api/v1/reports/${reportUuid}/publish`,
                    {},
                    {
                        headers: { Authorization: getAuthHeader() }
                    }
                );
                successCount++;
            } catch (error) {
                failCount++;
            }
        }

        setPublishLoading(false);
        handleClosePublishDialog();

        if (successCount > 0) {
            enqueueSnackbar(`Successfully published ${successCount} report(s)`, { variant: 'success' });
        }
        if (failCount > 0) {
            enqueueSnackbar(`Failed to publish ${failCount} report(s)`, { variant: 'error' });
        }

        // Refresh the reports list
        if (viewMode === 'all') {
            fetchAllReports();
        } else if (viewMode === 'student' && selectedStudent) {
            fetchStudentReports(selectedStudent.uuid);
        } else if (selectedCourse) {
            fetchReportsForCourse(selectedCourse.uuid);
        }
    };

    const getStatusChip = (status) => {
        const config = {
            pending: {
                icon: <HourglassEmptyIcon fontSize="small" />,
                color: 'default',
                label: 'Pending'
            },
            processing: {
                icon: <SyncIcon fontSize="small" sx={{
                    animation: 'spin 1s linear infinite',
                    '@keyframes spin': {
                        '0%': { transform: 'rotate(0deg)' },
                        '100%': { transform: 'rotate(360deg)' }
                    }
                }} />,
                color: 'info',
                label: 'Processing'
            },
            completed: {
                icon: <CheckCircleIcon fontSize="small" />,
                color: 'success',
                label: 'Completed'
            },
            failed: {
                icon: <ErrorIcon fontSize="small" />,
                color: 'error',
                label: 'Failed'
            },
            draft: {
                icon: <DraftIcon fontSize="small" />,
                color: 'warning',
                label: 'Draft'
            },
            published: {
                icon: <PublishIcon fontSize="small" />,
                color: 'primary',
                label: 'Published'
            }
        };

        const { icon, color, label } = config[status] || config.pending;
        return (
            <Chip
                icon={icon}
                label={label}
                color={color}
                size="small"
                sx={{ minWidth: 100 }}
            />
        );
    };

    // Filter reports (by type and status only - course is already filtered)
    const filteredReports = reports.filter((report) => {
        if (reportTypeFilter !== 'all' && report.report_type !== reportTypeFilter) {
            return false;
        }
        if (statusFilter !== 'all' && report.status !== statusFilter) {
            return false;
        }
        return true;
    });

    // Filter student reports
    const filteredStudentReports = studentReports.filter((report) => {
        if (reportTypeFilter !== 'all' && report.report_type !== reportTypeFilter) {
            return false;
        }
        if (statusFilter !== 'all' && report.status !== statusFilter) {
            return false;
        }
        return true;
    });

    // Determine which reports and loading state to display
    let displayedReports = [];
    let isLoading = false;
    if (viewMode === 'all') {
        displayedReports = allReports;
        isLoading = allReportsLoading;
    } else if (viewMode === 'student') {
        displayedReports = filteredStudentReports;
        isLoading = studentReportsLoading;
    } else {
        displayedReports = filteredReports;
        isLoading = reportsLoading;
    }

    if (coursesLoading) {
        return <Loading />;
    }

    return (
        <Box m="20px" marginX={3} marginY={3}>
            <Header title="Student Reports" subtitle="Manage AI-Generated Student Reports" />

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs value={tabValue} onChange={handleTabChange}>
                    <Tab label="Reports" />
                    <Tab label="Auto Reports" />
                    {process.env.NODE_ENV === 'development' && <Tab label="Dev Testing" />}
                    {process.env.NODE_ENV === 'development' && <Tab label="Logic Explainer" />}
                </Tabs>
            </Box>

            {tabValue === 0 && (
            <>
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {/* View Mode Toggle */}
            <Box mb={3}>
                <ToggleButtonGroup
                    value={viewMode}
                    exclusive
                    onChange={(e, newMode) => {
                        if (newMode) {
                            setViewMode(newMode);
                            // Reset pagination when switching to all mode
                            if (newMode === 'all') {
                                setPagination(prev => ({ ...prev, page: 1 }));
                            }
                        }
                    }}
                    size="small"
                >
                    <ToggleButton value="all">All Reports</ToggleButton>
                    <ToggleButton value="course">By Course</ToggleButton>
                    <ToggleButton value="student">By Student</ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {/* Filters and Actions */}
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3} flexWrap="wrap" gap={2}>
                <Box display="flex" gap={2} flexWrap="wrap">
                    {/* Year/Semester/Course filters - only in course mode */}
                    {viewMode === 'course' && (
                        <>
                            {/* Year */}
                            <Box>
                                <Dropdown
                                    maxWidth={150}
                                    maxHeight={3}
                                    reactUseState={year}
                                    setReactUseState={setYear}
                                    inputLabel="Year"
                                    data={generateYears(minYear, maxYear)}
                                />
                            </Box>

                            {/* Semester */}
                            <Box>
                                <Dropdown
                                    maxWidth={150}
                                    maxHeight={3}
                                    reactUseState={semester}
                                    setReactUseState={setSemester}
                                    inputLabel="Semester"
                                    data={semesters}
                                />
                            </Box>

                            {/* Course - shown after year/semester selected */}
                            {year && semester && (
                                <Box sx={{ minWidth: 300, maxWidth: 400 }}>
                                    <Autocomplete
                                        options={filteredCourses}
                                        getOptionLabel={(option) =>
                                            option.code ? `${option.code} - ${option.course_title}` : option.course_title
                                        }
                                        value={selectedCourse}
                                        onChange={(_, newValue) => {
                                            setSelectedCourse(newValue);
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Course"
                                                placeholder="Choose a course"
                                                size="small"
                                                helperText={`${filteredCourses.length} course${filteredCourses.length !== 1 ? 's' : ''} available`}
                                            />
                                        )}
                                        renderOption={(props, option) => (
                                            <li {...props} key={option.uuid}>
                                                {option.code ? `${option.code} - ${option.course_title}` : option.course_title}
                                            </li>
                                        )}
                                        noOptionsText={`No courses for ${year} ${semester}`}
                                        isOptionEqualToValue={(option, value) => option.uuid === value?.uuid}
                                        fullWidth
                                    />
                                </Box>
                            )}
                        </>
                    )}

                    {/* Student search - only in student mode */}
                    {viewMode === 'student' && (
                        <Box sx={{ minWidth: 400, maxWidth: 500 }}>
                            <Autocomplete
                                options={students}
                                getOptionLabel={(option) => {
                                    const parts = [];
                                    if (option.first_name) parts.push(option.first_name);
                                    if (option.last_name) parts.push(option.last_name);
                                    if (option.korean_name) parts.push(`(${option.korean_name})`);
                                    if (option.consultation_number) parts.push(`- #${option.consultation_number}`);
                                    return parts.join(' ');
                                }}
                                value={selectedStudent}
                                onChange={(_, newValue) => {
                                    setSelectedStudent(newValue);
                                }}
                                loading={studentsLoading}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Student"
                                        placeholder="Search by name or consultation number"
                                        size="small"
                                        helperText={`${students.length} student${students.length !== 1 ? 's' : ''} available`}
                                    />
                                )}
                                renderOption={(props, option) => (
                                    <li {...props} key={option.uuid}>
                                        {option.first_name} {option.last_name}
                                        {option.korean_name && ` (${option.korean_name})`}
                                        {option.consultation_number && ` - #${option.consultation_number}`}
                                    </li>
                                )}
                                noOptionsText="No students found"
                                isOptionEqualToValue={(option, value) => option.uuid === value?.uuid}
                                fullWidth
                            />
                        </Box>
                    )}
                </Box>

                {/* Action Buttons */}
                <Box display="flex" gap={2}>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => navigate('/admin/reports/generate')}
                    >
                        Generate Reports
                    </Button>
                    <Button
                        variant="contained"
                        color="success"
                        startIcon={<PublishIcon />}
                        onClick={handleOpenPublishDialog}
                    >
                        Publish Reports
                    </Button>
                </Box>
            </Box>

            {/* Reports Section (shown after course/student selected, or always in all mode) */}
            {(viewMode === 'all' || (viewMode === 'course' && selectedCourse) || (viewMode === 'student' && selectedStudent)) && (
                <>
                    {/* Actions Bar */}
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Box display="flex" gap={2}>
                            <TextField
                                select
                                label="Type"
                                value={reportTypeFilter}
                                onChange={(e) => {
                                    setReportTypeFilter(e.target.value);
                                    // Reset to page 1 when filter changes in all mode
                                    if (viewMode === 'all') {
                                        setPagination(prev => ({ ...prev, page: 1 }));
                                    }
                                }}
                                sx={{ minWidth: 130 }}
                                size="small"
                            >
                                <MenuItem value="all">All Types</MenuItem>
                                <MenuItem value="midterm">Midterm</MenuItem>
                                <MenuItem value="final">Final</MenuItem>
                            </TextField>

                            <TextField
                                select
                                label="Status"
                                value={statusFilter}
                                onChange={(e) => {
                                    setStatusFilter(e.target.value);
                                    // Reset to page 1 when filter changes in all mode
                                    if (viewMode === 'all') {
                                        setPagination(prev => ({ ...prev, page: 1 }));
                                    }
                                }}
                                sx={{ minWidth: 130 }}
                                size="small"
                            >
                                <MenuItem value="all">All</MenuItem>
                                <MenuItem value="pending">Pending</MenuItem>
                                <MenuItem value="processing">Processing</MenuItem>
                                <MenuItem value="completed">Completed</MenuItem>
                                <MenuItem value="failed">Failed</MenuItem>
                                <MenuItem value="draft">Draft</MenuItem>
                                <MenuItem value="published">Published</MenuItem>
                            </TextField>

                            <Tooltip title="Refresh">
                                <IconButton
                                    onClick={() => {
                                        if (viewMode === 'all') {
                                            fetchAllReports();
                                        } else if (viewMode === 'student' && selectedStudent) {
                                            fetchStudentReports(selectedStudent.uuid);
                                        } else if (selectedCourse) {
                                            fetchReportsForCourse(selectedCourse.uuid);
                                        }
                                    }}
                                    color="primary"
                                    size="small"
                                >
                                    <RefreshIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>

            {/* Reports Table */}
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            {(viewMode === 'all' || viewMode === 'student') && <StyledTableCell>Course</StyledTableCell>}
                            {viewMode !== 'student' && <StyledTableCell>Student</StyledTableCell>}
                            <StyledTableCell>Type</StyledTableCell>
                            <StyledTableCell>Status</StyledTableCell>
                            <StyledTableCell>Generated</StyledTableCell>
                            <StyledTableCell align="right">Actions</StyledTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {displayedReports.length === 0 ? (
                            <StyledTableRow>
                                <StyledTableCell colSpan={(viewMode === 'all' || viewMode === 'student') ? 6 : 5} align="center">
                                    <Typography variant="body2" color="text.secondary" py={3}>
                                        {viewMode === 'all'
                                            ? 'No reports found.'
                                            : viewMode === 'student'
                                            ? 'No reports found for this student.'
                                            : 'No reports found for this course.'}
                                    </Typography>
                                </StyledTableCell>
                            </StyledTableRow>
                        ) : (
                            displayedReports.map((report) => (
                                <StyledTableRow key={report.id}>
                                    {(viewMode === 'all' || viewMode === 'student') && (
                                        <StyledTableCell>
                                            {report.course_code
                                                ? `${report.course_code} - ${report.course_name}`
                                                : report.course_name || 'Unknown'}
                                        </StyledTableCell>
                                    )}
                                    {viewMode !== 'student' && (
                                        <StyledTableCell>
                                            {report.student_name || 'Unknown'}
                                        </StyledTableCell>
                                    )}
                                    <StyledTableCell>
                                        <Typography
                                            variant="body2"
                                            sx={{ textTransform: 'capitalize' }}
                                        >
                                            {report.report_type}
                                        </Typography>
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        {getStatusChip(report.status)}
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        {report.generated_at
                                            ? new Date(report.generated_at).toLocaleDateString()
                                            : 'N/A'}
                                    </StyledTableCell>
                                    <StyledTableCell align="right">
                                        <Box display="flex" justifyContent="flex-end" gap={1}>
                                            {(report.status === 'completed' || report.status === 'draft' || report.status === 'published') && (
                                                <Tooltip title="View Report">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => navigate(`/admin/reports/${report.uuid}`)}
                                                    >
                                                        <VisibilityIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {report.status === 'failed' && (
                                                <Tooltip title="Retry">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleRetryReport(report.uuid)}
                                                        color="warning"
                                                    >
                                                        <RefreshIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            <Tooltip title="Delete">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                        setReportToDelete(report.uuid);
                                                        setDeleteDialogOpen(true);
                                                    }}
                                                    color="error"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </StyledTableCell>
                                </StyledTableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {isLoading && (
                <Box display="flex" justifyContent="center" p={3}>
                    <Loading />
                </Box>
            )}

            {/* Pagination - only in all mode */}
            {viewMode === 'all' && pagination.totalPages > 1 && (
                <Box display="flex" justifyContent="center" mt={3}>
                    <Pagination
                        count={pagination.totalPages}
                        page={pagination.page}
                        onChange={(_, page) => setPagination(prev => ({ ...prev, page }))}
                        color="primary"
                        showFirstButton
                        showLastButton
                    />
                </Box>
            )}
            </>
            )}

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => {
                    setDeleteDialogOpen(false);
                    setReportToDelete(null);
                }}
            >
                <DialogTitle>Delete Report</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this report? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setDeleteDialogOpen(false);
                            setReportToDelete(null);
                        }}
                    >
                        Cancel
                    </Button>
                    <Button onClick={handleDeleteReport} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Publish Reports Dialog */}
            <Dialog
                open={publishDialogOpen}
                onClose={handleClosePublishDialog}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Publish Reports</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Select the completed reports you want to publish. Published reports will be visible to parents.
                    </DialogContentText>

                    {fetchingCompletedReports ? (
                        <Box display="flex" justifyContent="center" p={3}>
                            <Loading />
                        </Box>
                    ) : completedReports.length === 0 ? (
                        <Alert severity="info">
                            No completed reports available to publish.
                        </Alert>
                    ) : (
                        <>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={selectedReportsToPublish.length === completedReports.length}
                                        indeterminate={selectedReportsToPublish.length > 0 && selectedReportsToPublish.length < completedReports.length}
                                        onChange={handleToggleSelectAll}
                                    />
                                }
                                label={`Select All (${completedReports.length} reports)`}
                                sx={{ mb: 2 }}
                            />
                            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            <StyledTableCell padding="checkbox" />
                                            <StyledTableCell>Student</StyledTableCell>
                                            <StyledTableCell>Course</StyledTableCell>
                                            <StyledTableCell>Type</StyledTableCell>
                                            <StyledTableCell>Generated</StyledTableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {completedReports.map((report) => (
                                            <StyledTableRow
                                                key={report.uuid}
                                                hover
                                                onClick={() => handleToggleReportSelection(report.uuid)}
                                                sx={{ cursor: 'pointer' }}
                                            >
                                                <StyledTableCell padding="checkbox">
                                                    <Checkbox
                                                        checked={selectedReportsToPublish.includes(report.uuid)}
                                                        onClick={(e) => e.stopPropagation()}
                                                        onChange={() => handleToggleReportSelection(report.uuid)}
                                                    />
                                                </StyledTableCell>
                                                <StyledTableCell>{report.student_name || 'Unknown'}</StyledTableCell>
                                                <StyledTableCell>
                                                    {report.course_code
                                                        ? `${report.course_code} - ${report.course_name}`
                                                        : report.course_name || 'Unknown'}
                                                </StyledTableCell>
                                                <StyledTableCell sx={{ textTransform: 'capitalize' }}>
                                                    {report.report_type}
                                                </StyledTableCell>
                                                <StyledTableCell>
                                                    {report.generated_at
                                                        ? new Date(report.generated_at).toLocaleDateString()
                                                        : 'N/A'}
                                                </StyledTableCell>
                                            </StyledTableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                                {selectedReportsToPublish.length} of {completedReports.length} reports selected
                            </Typography>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClosePublishDialog} disabled={publishLoading}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleBulkPublish}
                        color="success"
                        variant="contained"
                        disabled={publishLoading || selectedReportsToPublish.length === 0}
                        startIcon={<PublishIcon />}
                    >
                        {publishLoading ? 'Publishing...' : `Publish ${selectedReportsToPublish.length} Reports`}
                    </Button>
                </DialogActions>
            </Dialog>
            </>
            )}

            {tabValue === 1 && (
                <AutoReports />
            )}

            {tabValue === 2 && process.env.NODE_ENV === 'development' && (
                <DevAutoReportTesting />
            )}

            {tabValue === 3 && process.env.NODE_ENV === 'development' && (
                <AutoReportExplainer />
            )}
        </Box>
    );
};

export default Reports;
